// Vue core libraries - Pinia, VueUse & Vue
import { acceptHMRUpdate, defineStore } from "pinia";
import { computed, ref } from "vue";

// TMI - content type and query definitions
import { SearchableTicker } from "@tmi/types/entities";
import { cache, CacheClosedError, tickers } from "@/services/cache";
import { createInitLatch } from "@/stores/init-latch";

// Non-reactive variables
import { createDefaultLogger } from "@/types/console-logger";
const logger = createDefaultLogger();

// State values
const initialized = computed< boolean >( () => allTickers.value.length > 0 );
const loading = ref< boolean >( false );
// tmi-7be: a cache failure stops initialization until reset(), so no watcher can re-run it in a loop.
const initLatch = createInitLatch();

const defaultTickers: SearchableTicker[] = [];
const allTickers = ref< SearchableTicker[] >( defaultTickers );
const searchableEtfs = ref< SearchableTicker[] >( defaultTickers );
const searchableStocks = ref< SearchableTicker[] >( defaultTickers );

export const useTickersStore = defineStore( 'tickers', () => {
    function reset(): void {
        allTickers.value.splice( 0 );
        searchableEtfs.value.splice( 0 );
        searchableStocks.value.splice( 0 );
        loading.value = false;
        initLatch.clear();
    }

    async function initialize(): Promise< void > {
        if ( initialized.value || loading.value || initLatch.isHalted() ) {
            logger.debug( '[ Tickers ].initialize() exiting - already processing...' );
            return;
        }

        try {
            logger.debug( `[ Tickers ] Checking the local database for any tickers...` );

            loading.value = true;

            const t0: number = performance.now();

            // Wait for the database to open before the first read.
            await cache.ready();

            const uniqueEtfs: Set< SearchableTicker > = new Set< SearchableTicker >();
            const uniqueStocks: Set< SearchableTicker > = new Set< SearchableTicker >();
            if ( !await tickers.isEmpty() ) {
                // Clear the contents of the searchable collections
                allTickers.value.splice( 0 );
                searchableEtfs.value.splice( 0 );
                searchableStocks.value.splice( 0 );

                for ( const ticker of await tickers.all() ) {
                    ticker.isEtf ? uniqueEtfs.add( ticker ) : uniqueStocks.add( ticker );
                }

                searchableStocks.value = [ ...uniqueStocks ];
                searchableEtfs.value = [ ...uniqueEtfs ];
                allTickers.value = [ ...uniqueStocks, ...uniqueEtfs ];

                const t1: number = performance.now();
                logger.info( `(local) Loaded ${ searchableStocks.value.length } stock & ${ searchableEtfs.value.length } etf tickers in ${ t1 - t0 } ms` );
            }
        } catch( err: any ) {
            if ( err instanceof CacheClosedError ) { initLatch.halt( err ); return; }   // a newer build upgraded the cache; this tab is reloading
            logger.error( JSON.stringify( err.message ) )
            reset();
            initLatch.halt( err );   // after reset(), which clears it: a cache failure stays latched
        } finally {
            // Clear the "loading" flag
            loading.value = false;
        }
    }

    function tickerById( id: string ): SearchableTicker | undefined {
        return allTickers.value.find( ( ticker: SearchableTicker ): boolean => ticker.id === id );
    }

    // C5 (Option 1): the symbol-side half of the content-id -> symbol -> ticker crosswalk lookup
    // used by content-hydration.ts. Exact match against `ticker` — the sentient catalog's symbols
    // are already normalized, same as the pre-existing `tickerById` lookup above.
    function tickerBySymbol( symbol: string ): SearchableTicker | undefined {
        return allTickers.value.find( ( ticker: SearchableTicker ): boolean => ticker.ticker === symbol );
    }

    return {
        // ACTIONS
        initialize,
        reset,
        tickerById,
        tickerBySymbol,

        // GETTERS
        allTickers,
        searchableEtfs,
        searchableStocks
    }
} );

if ( import.meta.hot ) {
    import.meta.hot.accept( acceptHMRUpdate( useTickersStore, import.meta.hot ) );
}
